import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "@/context/AuthContext";
import { Copy, Check, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function Objections() {
    const { authHeaders } = useAuth();
    const [items, setItems] = useState([]);
    const [activeCat, setActiveCat] = useState("all");
    const [copied, setCopied] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API}/templates/objections`, { headers: authHeaders() });
                setItems(res.data);
            } catch { /* noop */ }
        })();
    }, [authHeaders]);

    const categories = useMemo(
        () => ["all", ...Array.from(new Set(items.map((i) => i.category)))],
        [items]
    );
    const filtered = activeCat === "all" ? items : items.filter((i) => i.category === activeCat);

    const copy = async (text, id) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(id);
            toast.success("Copied");
            setTimeout(() => setCopied(null), 1200);
        } catch {
            toast.error("Copy failed");
        }
    };

    return (
        <div data-testid="objections-page">
            <div className="mb-8">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-signal mb-2">/ OBJECTION VAULT</div>
                <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tight">Handle the pushback.</h1>
                <p className="text-zinc-400 mt-2">Battle-tested frameworks. Customise the variables in <code className="text-signal font-mono">{"{braces}"}</code> and ship.</p>
            </div>

            <div className="flex flex-wrap gap-2 mb-6" data-testid="objection-filters">
                {categories.map((c) => (
                    <button
                        key={c}
                        onClick={() => setActiveCat(c)}
                        data-testid={`objection-filter-${c}`}
                        className={`px-3.5 py-2 text-xs font-mono uppercase tracking-widest rounded-md border transition-colors ${
                            activeCat === c
                                ? "border-signal bg-signal/10 text-white"
                                : "border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700"
                        }`}
                    >
                        {c}
                    </button>
                ))}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                {filtered.map((t) => (
                    <div key={t.id} className="border border-zinc-900 bg-[#0c0c0c] p-5" data-testid={`objection-${t.id}`}>
                        <div className="flex items-center gap-2 mb-3">
                            <ShieldAlert className="w-4 h-4 text-signal" strokeWidth={1.5} />
                            <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">{t.category}</span>
                        </div>
                        <h3 className="font-heading font-bold text-lg mb-3">{t.title}</h3>
                        <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap mb-4">{t.body}</p>
                        <button
                            onClick={() => copy(t.body, t.id)}
                            data-testid={`objection-copy-${t.id}`}
                            className="inline-flex items-center gap-2 border border-zinc-800 hover:border-zinc-600 text-zinc-300 hover:text-white px-3 py-1.5 rounded-md text-xs transition-colors"
                        >
                            {copied === t.id ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy template</>}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}
